import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { openPrintWindow } from "@/utils/printHelper";
import { describeProductCode } from "@/utils/productCodeDescription";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ArrowLeft, Plus, Trash2, Printer, FileText, ChevronDown, Check, RefreshCw, Building2, MapPin, Save, Mail, Phone, Pencil, Boxes } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useSubscription } from "@/context/SubscriptionContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Company Logo URL
const LOGO_URL = "/company-logo.png";

const StorePurchaseOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Company info (static)
  const companyInfo = {
    name: "CHALLENGE PACKAGING INDUSTRIES",
    location: "Tamil Nadu, India",
    phone: "7299808164",
    email: "info@challengepack.in",
    gstin: "33AGSPV7578C2ZP"
  };

  // Form state
  const [poNumber, setPoNumber] = useState("");
  const [poDate, setPoDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryDate, setDeliveryDate] = useState("");
  
  // Vendor info
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [vendorCompanyName, setVendorCompanyName] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [vendorAddress, setVendorAddress] = useState("");
  const [vendorGstin, setVendorGstin] = useState("");
  const [vendorEmail, setVendorEmail] = useState("");
  const [vendorPhone, setVendorPhone] = useState("");
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  
  // Delivery details (optional)
  const [enableDeliveryDetails, setEnableDeliveryDetails] = useState(false);
  const [deliveryName, setDeliveryName] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryGstin, setDeliveryGstin] = useState("");
  
  // Stable per-row uid for React keys — survives reorder/delete without the
  // index-as-key bug (input focus jumping to the wrong row after a delete).
  // crypto.randomUUID is available in all modern browsers; falls back to a
  // numeric-time uid for the (very unlikely) older runtime case.
  const newRowUid = () => (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  // Line items. `size` carries the freeform numeric/text size value (e.g.
  // "120", "39.5", or "32x12" for joint dims) and `sizeUnit` is the unit-only
  // dropdown ("mm" / "cm" / "inches"). Added Feb 2026.
  const [lineItems, setLineItems] = useState([
    { _uid: newRowUid(), id: 1, selectedItem: "", description: "", hsn: "", qty: "", unit: "kg", size: "", sizeUnit: "Inches", rate: "", amount: 0 }
  ]);
  
  // Tax rates
  const [cgstRate, setCgstRate] = useState(9);
  const [sgstRate, setSgstRate] = useState(9);
  
  // Notes
  const [notes, setNotes] = useState("");
  
  // Terms & Conditions
  const [termsConditions, setTermsConditions] = useState([]);
  const [newTerm, setNewTerm] = useState("");
  const [editingTermIndex, setEditingTermIndex] = useState(null);
  const [editingTermText, setEditingTermText] = useState("");
  
  // Data from API
  const [materials, setMaterials] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  
  // Dropdown states
  const [itemDropdownOpen, setItemDropdownOpen] = useState({});
  const [hsnDropdownOpen, setHsnDropdownOpen] = useState({});
  const [deliveryVendorOpen, setDeliveryVendorOpen] = useState(false);
  const [deliveryVendorSearch, setDeliveryVendorSearch] = useState("");

  // Inventory Control prefill
  const { hasFeature } = useSubscription();
  const canUseInventoryControl = hasFeature("inventory_control");
  const [prefillPayload, setPrefillPayload] = useState(null);  // { items, meta }
  const [prefillDialogOpen, setPrefillDialogOpen] = useState(false);
  const [prefillApplying, setPrefillApplying] = useState(false);

  useEffect(() => {
    fetchMaterials();
    fetchVendors();
    fetchHSNCodes();
    fetchLowStock();
    
    if (editId) {
      loadPOForEdit(editId);
    } else {
      generatePONumber();
    }
  }, [editId]);

  // Detect Inventory Control hand-off (?prefill=inv-control + sessionStorage payload)
  useEffect(() => {
    if (searchParams.get("prefill") !== "inv-control") return;
    try {
      const raw = sessionStorage.getItem("cpi_inv_control_payload");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.items?.length) return;
      setPrefillPayload(parsed);
      setPrefillDialogOpen(true);
    } catch (e) {
      console.error("Inventory Control prefill parse failed:", e);
    }
  }, [searchParams]);

  // Resolve a calculated row -> a PO line item.
  // 1. Match an existing material by product_code (case-insensitive)
  // 2. Otherwise auto-create one via POST /api/materials
  // 3. On any failure, return a "pending" row the user can map manually.
  const resolveCalculatedItem = async (item, materialsList) => {
    const code = (item.rmName || "").trim();
    const match = materialsList.find(
      (m) => (m.product_code || "").toLowerCase() === code.toLowerCase()
    );
    if (match) {
      return {
        material: match,
        created: false,
        line: buildLineFromMaterial(match, item),
      };
    }
    // Try create
    try {
      const res = await axios.post(`${BACKEND_URL}/api/materials`, {
        product_code: code,
        item_name: item.description || code,
        product_type: "Paper",
        hsn_code: "48043100",
        buying_price: 0,
      });
      return {
        material: res.data,
        created: true,
        line: buildLineFromMaterial(res.data, item),
      };
    } catch (err) {
      // Probably "already exists" (race) or transient — re-fetch and retry once
      try {
        const refetched = await axios.get(`${BACKEND_URL}/api/materials`);
        const m = (refetched.data || []).find(
          (x) => (x.product_code || "").toLowerCase() === code.toLowerCase()
        );
        if (m) {
          return { material: m, created: false, line: buildLineFromMaterial(m, item) };
        }
      } catch (e2) {
        console.error("Refetch materials failed:", e2);
      }
      return {
        material: null,
        created: false,
        line: {
          id: 0,  // re-keyed when appended
          selectedItem: "",
          description: `${code} (auto-create failed — please pick manually)`,
          hsn: "",
          qty: item.qtyKg ? String(item.qtyKg) : "",
          unit: "kg",
          size: "",
          sizeUnit: "Inches",
          rate: "",
          amount: 0,
        },
      };
    }
  };

  const buildLineFromMaterial = (m, item) => {
    const code = m.product_code || m.item_name || "";
    const itemLabel = `${code} - ${m.product_type || ""} ${m.reel_size_inches ? `(${m.reel_size_inches}")` : ""}`.trim();
    const qty = item.qtyKg || 0;
    const rate = m.buying_price && m.buying_price > 0 ? m.buying_price : 0;
    const parsedDesc = describeProductCode(code) || itemLabel;
    const sizeNum = m.reel_size_inches ? String(m.reel_size_inches) : "";
    const desc = sizeNum ? `${parsedDesc} - ${sizeNum} Inches` : parsedDesc;
    return {
      id: 0,  // re-keyed when appended
      selectedItem: itemLabel,
      description: desc,
      hsn: m.hsn_code || "48043100",
      qty: qty ? String(qty) : "",
      unit: "kg",
      size: sizeNum,
      sizeUnit: sizeNum ? "Inches" : "",
      rate: rate ? String(rate) : "",
      amount: qty * rate,
    };
  };

  const applyInventoryControlPrefill = async () => {
    if (!prefillPayload?.items?.length) return;
    setPrefillApplying(true);
    try {
      // Always fetch the freshest material list before matching
      let materialsList = materials;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/materials`);
        materialsList = r.data || materials;
        setMaterials(materialsList);
      } catch (e) {
        console.error("Materials refresh failed:", e);
      }

      const resolved = [];
      for (const item of prefillPayload.items) {
        const r = await resolveCalculatedItem(item, materialsList);
        if (r.created && r.material) materialsList = [...materialsList, r.material];
        resolved.push(r);
      }
      // Refresh dropdown source so newly-created materials are pickable
      setMaterials(materialsList);

      // Append (existing rows preserved). Drop a single empty seed row if present.
      const baseRows = (lineItems.length === 1 && !lineItems[0].selectedItem && !lineItems[0].description && !lineItems[0].qty)
        ? []
        : [...lineItems];
      const startId = baseRows.length;
      const newRows = resolved.map((r, i) => ({ ...r.line, _uid: newRowUid(), id: startId + i + 1 }));
      setLineItems([...baseRows, ...newRows]);

      const matched = resolved.filter((r) => r.material && !r.created).length;
      const created = resolved.filter((r) => r.created).length;
      const failed = resolved.filter((r) => !r.material).length;
      toast.success(
        `Appended ${resolved.length} item(s) — ${matched} matched, ${created} auto-created${failed ? `, ${failed} need manual mapping` : ""}`
      );

      // Cleanup hand-off
      sessionStorage.removeItem("cpi_inv_control_payload");
      setPrefillPayload(null);
      setPrefillDialogOpen(false);
      // Strip query so a refresh doesn't re-trigger
      navigate("/store/purchase-order/new", { replace: true });
    } catch (e) {
      console.error("Apply prefill failed:", e);
      toast.error("Failed to import calculated items");
    } finally {
      setPrefillApplying(false);
    }
  };

  const cancelInventoryControlPrefill = () => {
    sessionStorage.removeItem("cpi_inv_control_payload");
    setPrefillPayload(null);
    setPrefillDialogOpen(false);
    navigate("/store/purchase-order/new", { replace: true });
  };

  const loadPOForEdit = async (poId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/purchase-orders/${poId}`);
      const po = response.data;
      
      setIsEditMode(true);
      setPoNumber(po.po_number || "");
      setPoDate(po.po_date || new Date().toISOString().split('T')[0]);
      setDeliveryDate(po.delivery_date || "");
      setSelectedVendorId(po.vendor_id || "");
      setVendorCompanyName(po.vendor_company_name || "");
      setVendorName(po.vendor_name || "");
      setVendorAddress(po.vendor_address || "");
      setVendorGstin(po.vendor_gstin || "");
      setVendorEmail(po.vendor_email || "");
      setVendorPhone(po.vendor_phone || "");
      
      if (po.delivery_name || po.delivery_address) {
        setEnableDeliveryDetails(true);
        setDeliveryName(po.delivery_name || "");
        setDeliveryAddress(po.delivery_address || "");
        setDeliveryGstin(po.delivery_gstin || "");
      }
      
      if (po.items && po.items.length > 0) {
        setLineItems(po.items.map((item, idx) => ({
          _uid: newRowUid(),
          id: idx + 1,
          selectedItem: item.selectedItem || "",
          description: item.description || "",
          hsn: item.hsn || "",
          qty: item.qty || "",
          unit: item.unit || "kg",
          rate: item.rate || "",
          amount: item.amount || 0
        })));
      }
      
      setCgstRate(po.cgst_rate || 9);
      setSgstRate(po.sgst_rate || 9);
      setNotes(po.notes || "");
      setTermsConditions(po.terms_conditions || []);
    } catch (error) {
      toast.error("Failed to load PO for editing");
      console.error(error);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/materials`);
      setMaterials(response.data);
    } catch (error) {
      console.error("Error fetching materials:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const fetchHSNCodes = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/hsn-codes/seed-defaults`);
      const response = await axios.get(`${BACKEND_URL}/api/hsn-codes`);
      setHsnCodes(response.data);
    } catch (error) {
      console.error("Error fetching HSN codes:", error);
    }
  };

  const fetchLowStock = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/materials/low-stock`);
      setLowStockItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching low stock:", error);
    }
  };

  const generatePONumber = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/purchase-orders/next-number`);
      setPoNumber(response.data.next_po_number);
    } catch (error) {
      console.error("Error generating PO number:", error);
      // Fallback to local generation
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      let fyStart, fyEnd;
      if (month >= 4) {
        fyStart = year;
        fyEnd = year + 1;
      } else {
        fyStart = year - 1;
        fyEnd = year;
      }
      const fyString = `${(fyStart % 100).toString().padStart(2, '0')}-${(fyEnd % 100).toString().padStart(2, '0')}`;
      setPoNumber(`PO/${fyString}/001`);
    }
  };

  // Select vendor from dropdown
  const selectVendor = (vendor) => {
    setSelectedVendorId(vendor.id);
    setVendorCompanyName(vendor.company_name);
    setVendorName(vendor.vendor_name || "");
    setVendorAddress(vendor.address || "");
    setVendorGstin(vendor.gstin || "");
    setVendorEmail(vendor.email || "");
    setVendorPhone(vendor.phone || "");
    setVendorDropdownOpen(false);
  };

  // Calculate line item amount
  const calculateAmount = (qty, rate) => {
    const q = parseFloat(qty) || 0;
    const r = parseFloat(rate) || 0;
    return q * r;
  };

  // Update line item
  const updateLineItem = (index, field, value) => {
    const updatedItems = [...lineItems];

    // Two virtual fields used by the inline size prompt in the cell render:
    //   _appendSize: fold size + unit into description (called onBlur of the
    //                Size input — only takes effect if both are non-empty).
    //   _clearSize:  user clicked the "Change size" link to re-edit. Wipe
    //                size+unit so the inputs reappear, AND strip the trailing
    //                " - {size} {unit}" segment from the description so the
    //                rebuild on next blur produces a clean string.
    if (field === '_appendSize' || field === '_lockSize') {
      const row = updatedItems[index];
      const unit = row.sizeUnit || 'Inches';
      if (row.size && row.description && !row.description.includes(` - ${row.size} ${unit}`)) {
        // Strip any older size suffix first so re-edit doesn't double up.
        const stripped = row.description.replace(/\s*-\s*[\d.]+\s*(mm|cm|Inches|inches|in)\s*$/i, '').trim();
        row.description = `${stripped} - ${row.size} ${unit}`;
      }
      row.sizeUnit = unit;
      if (field === '_lockSize' && row.size) {
        row.sizeLocked = true;
      }
      setLineItems(updatedItems);
      return;
    }
    if (field === '_clearSize') {
      const row = updatedItems[index];
      row.description = (row.description || '').replace(/\s*-\s*[\d.]+\s*(mm|cm|Inches|inches|in)\s*$/i, '').trim();
      row.size = '';
      row.sizeUnit = '';
      row.sizeLocked = false;
      setLineItems(updatedItems);
      return;
    }

    updatedItems[index][field] = value;
    
    if (field === 'qty' || field === 'rate') {
      updatedItems[index].amount = calculateAmount(
        updatedItems[index].qty,
        updatedItems[index].rate
      );
    }
    
    // If user picks a unit AFTER typing size, fold immediately (skip blur).
    if (field === 'sizeUnit') {
      const row = updatedItems[index];
      if (row.size && row.description && !row.description.includes(` - ${row.size} ${row.sizeUnit}`)) {
        const stripped = row.description.replace(/\s*-\s*[\d.]+\s*(mm|cm|Inches|inches|in)\s*$/i, '').trim();
        row.description = `${stripped} - ${row.size} ${row.sizeUnit}`;
      }
    }
    
    setLineItems(updatedItems);
  };

  // Add new line item
  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { _uid: newRowUid(), id: lineItems.length + 1, selectedItem: "", description: "", hsn: "", qty: "", unit: "kg", size: "", sizeUnit: "Inches", rate: "", amount: 0 }
    ]);
  };

  // Remove line item
  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  // Select material for line item
  const selectMaterial = (index, material) => {
    const updatedItems = [...lineItems];
    const code = material.product_code || material.item_name || "";
    // selectedItem stays as the operator-facing label (code + type + reel)
    const itemLabel = `${code} - ${material.product_type || ''} ${material.reel_size_inches ? `(${material.reel_size_inches}")` : ''}`.trim();
    updatedItems[index].selectedItem = itemLabel;
    // Description: auto-derive from product code per customer convention
    // (NS12016 → "Natural Shade - 120GSM - 16BF", etc.). Operator can edit
    // the field manually after — we never overwrite once changed because
    // selectMaterial is only called when a new item is picked.
    updatedItems[index].description = describeProductCode(code) || itemLabel;
    updatedItems[index].hsn = material.hsn_code || "48043100";
    // Auto-populate rate from material's buying_price
    if (material.buying_price && material.buying_price > 0) {
      updatedItems[index].rate = material.buying_price.toString();
      // Recalculate amount if qty exists
      const qty = parseFloat(updatedItems[index].qty) || 0;
      updatedItems[index].amount = qty * material.buying_price;
    }
    // If the material record carries a reel/size value, default it into the
    // size slot too (operator can override) and immediately fold into the
    // description so the inline size prompt is auto-resolved.
    if (material.reel_size_inches && !updatedItems[index].size) {
      updatedItems[index].size = String(material.reel_size_inches);
      updatedItems[index].sizeUnit = "Inches";
      const baseDesc = updatedItems[index].description;
      if (baseDesc && !baseDesc.includes(` - ${updatedItems[index].size} Inches`)) {
        updatedItems[index].description = `${baseDesc} - ${updatedItems[index].size} Inches`;
      }
      updatedItems[index].sizeLocked = true;
    }
    setLineItems(updatedItems);
    setItemDropdownOpen({ ...itemDropdownOpen, [index]: false });
  };

  // Select HSN code for line item
  const selectHSN = (index, hsn) => {
    const updatedItems = [...lineItems];
    updatedItems[index].hsn = hsn.code;
    setLineItems(updatedItems);
    setHsnDropdownOpen({ ...hsnDropdownOpen, [index]: false });
  };

  // Calculations
  const subTotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const cgstAmount = (subTotal * cgstRate) / 100;
  const sgstAmount = (subTotal * sgstRate) / 100;
  const grandTotal = subTotal + cgstAmount + sgstAmount;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount).replace('₹', '₹ ');
  };

  // Print PO
  const handlePrint = () => {
    // Line items with item name + description
    const lineItemsHTML = lineItems.map((item, idx) => {
      const itemDisplay = item.selectedItem 
        ? `<strong>${item.selectedItem}</strong>${item.description ? `<br><span style="font-size: 12px; color: #6b7280;">${item.description}</span>` : ''}`
        : (item.description || '-');
      return `
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${idx + 1}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${itemDisplay}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.hsn || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">${item.qty ? `${item.qty} ${item.unit}` : '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">${item.rate || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 600;">${formatCurrency(item.amount)}</td>
        </tr>
      `;
    }).join('');

    // Default delivery address when not enabled
    const defaultDeliveryAddress = "Challenge Packaging Industries, Sfno - 332/1 A, Sri Visalakshi Nagar, Mettukuppam Main, Vanagaram, Chennai - 600 095 (Opp to Captain TV Tasmac)";

    // Delivery section HTML
    const deliverySectionHTML = enableDeliveryDetails ? `
      <div class="info-box">
        <h3>Deliver To</h3>
        <p>
          <span class="highlight">${deliveryName || 'Not Specified'}</span><br>
          ${deliveryAddress ? `${deliveryAddress}<br>` : ''}
          ${deliveryGstin ? `GSTIN: ${deliveryGstin}` : ''}
        </p>
      </div>
    ` : `
      <div class="info-box">
        <h3>Deliver To</h3>
        <p>
          <span class="highlight">Challenge Packaging Industries</span><br>
          ${defaultDeliveryAddress}
        </p>
      </div>
    `;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Purchase Order - ${poNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            padding: 20px;
            color: #1f2937;
            background: #fff;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body { padding: 0; }
            .po-container { border: none; box-shadow: none; }
            .po-header {
              background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%) !important;
              -webkit-print-color-adjust: exact !important;
            }
            .items-table th {
              background: #FEF3C7 !important;
              -webkit-print-color-adjust: exact !important;
            }
            .totals-section {
              background: #f9fafb !important;
              -webkit-print-color-adjust: exact !important;
            }
            .total-row.grand {
              background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%) !important;
              -webkit-print-color-adjust: exact !important;
            }
            .footer {
              background: #FEF3C7 !important;
              -webkit-print-color-adjust: exact !important;
            }
          }
          .po-container {
            max-width: 800px;
            margin: 0 auto;
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            overflow: hidden;
          }
          .po-header {
            background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
            color: white;
            padding: 24px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .company-section {
            display: flex;
            align-items: center;
            gap: 16px;
          }
          .company-logo {
            width: 60px;
            height: 60px;
            border-radius: 8px;
            background: white;
            padding: 4px;
          }
          .company-name {
            font-size: 18px;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .company-details {
            font-size: 11px;
            opacity: 0.9;
            line-height: 1.5;
          }
          .po-title {
            text-align: right;
          }
          .po-title h1 {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .po-number {
            font-size: 14px;
            background: rgba(255,255,255,0.2);
            padding: 4px 12px;
            border-radius: 4px;
            display: inline-block;
          }
          .info-section {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
            padding: 20px 24px;
            background: #f9fafb;
            border-bottom: 1px solid #e5e7eb;
          }
          .info-box {
            padding: 16px;
            background: white;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }
          .info-box h3 {
            font-size: 11px;
            text-transform: uppercase;
            color: #6b7280;
            margin-bottom: 8px;
            letter-spacing: 0.5px;
          }
          .info-box p {
            font-size: 13px;
            line-height: 1.6;
          }
          .info-box .highlight {
            font-weight: 600;
            color: #D97706;
          }
          .dates-row {
            display: flex;
            gap: 30px;
            padding: 16px 24px;
            background: white;
            border-bottom: 1px solid #e5e7eb;
          }
          .date-item {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .date-label {
            font-size: 12px;
            color: #6b7280;
          }
          .date-value {
            font-size: 14px;
            font-weight: 600;
            color: #1f2937;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
          }
          .items-table th {
            background: #FEF3C7;
            padding: 14px 12px;
            text-align: left;
            font-size: 11px;
            text-transform: uppercase;
            color: #92400E;
            letter-spacing: 0.5px;
            border-bottom: 2px solid #F59E0B;
          }
          .items-table th:nth-child(1) { text-align: center; width: 40px; }
          .items-table th:nth-child(3) { text-align: center; }
          .items-table th:nth-child(4),
          .items-table th:nth-child(5),
          .items-table th:nth-child(6) { text-align: right; }
          .totals-section {
            display: flex;
            justify-content: flex-end;
            padding: 20px 24px;
            background: #f9fafb;
          }
          .totals-box {
            width: 300px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            border-bottom: 1px solid #e5e7eb;
            font-size: 14px;
          }
          .total-row.grand {
            border-bottom: none;
            background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
            color: white;
            margin: 12px -16px -16px;
            padding: 16px;
            border-radius: 0 0 8px 8px;
            font-size: 18px;
            font-weight: 700;
          }
          .signature-section {
            padding: 30px 24px;
            text-align: right;
            border-top: 1px solid #e5e7eb;
          }
          .signature-box {
            display: inline-block;
            text-align: center;
          }
          .signature-line {
            width: 200px;
            border-bottom: 2px solid #1f2937;
            margin-bottom: 8px;
            height: 60px;
          }
          .signature-label {
            font-size: 12px;
            color: #6b7280;
          }
          .footer {
            text-align: center;
            padding: 16px;
            background: #FEF3C7;
            font-size: 11px;
            color: #92400E;
          }
          @media print {
            body { padding: 0; }
            .po-container { border: none; box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="po-container">
          <div class="po-header">
            <div class="company-section">
              <img src="${LOGO_URL}" alt="CPI Logo" class="company-logo" />
              <div>
                <div class="company-name">${companyInfo.name}</div>
                <div class="company-details">
                  ${companyInfo.location}<br>
                  Phone: ${companyInfo.phone} | Email: ${companyInfo.email}<br>
                  GSTIN: ${companyInfo.gstin}
                </div>
              </div>
            </div>
            <div class="po-title">
              <h1>Purchase Order</h1>
              <span class="po-number">${poNumber}</span>
            </div>
          </div>
          
          <div class="info-section">
            <div class="info-box">
              <h3>Vendor</h3>
              <p>
                <span class="highlight">${vendorCompanyName || 'Not Specified'}</span><br>
                ${vendorName ? `Contact: ${vendorName}<br>` : ''}
                ${vendorAddress ? `${vendorAddress}<br>` : ''}
                ${vendorGstin ? `GSTIN: ${vendorGstin}` : ''}
              </p>
            </div>
            ${deliverySectionHTML}
            <div class="info-box">
              <h3>PO Details</h3>
              <p>
                <strong>PO Date:</strong> ${poDate}<br>
                <strong>Delivery Date:</strong> ${deliveryDate || 'TBD'}
              </p>
            </div>
          </div>
          
          <table class="items-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item & Description</th>
                <th>HSN/SAC</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${lineItemsHTML}
            </tbody>
          </table>
          
          <div class="totals-section">
            <div class="totals-box">
              <div class="total-row">
                <span>Sub Total</span>
                <span>${formatCurrency(subTotal)}</span>
              </div>
              <div class="total-row">
                <span>CGST (${cgstRate}%)</span>
                <span>${formatCurrency(cgstAmount)}</span>
              </div>
              <div class="total-row">
                <span>SGST (${sgstRate}%)</span>
                <span>${formatCurrency(sgstAmount)}</span>
              </div>
              <div class="total-row grand">
                <span>Grand Total</span>
                <span>${formatCurrency(grandTotal)}</span>
              </div>
            </div>
          </div>
          
          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">Authorized Signature</div>
            </div>
          </div>
          
          <div class="footer">
            Challenge Packaging Industries - Purchase Order
          </div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    openPrintWindow(htmlContent);
    toast.success("Opening print dialog...");
  };

  // Send PO via Email (Backend API with PDF attachment)
  const handleSendEmail = async () => {
    if (!vendorEmail) {
      toast.error("Vendor email not available. Please update vendor details in Database.");
      return;
    }
    
    // First save the PO if not already saved
    if (!isEditMode && !editId) {
      toast.error("Please save the PO first before sending email.");
      return;
    }
    
    const poId = editId || await savePOAndGetId();
    if (!poId) return;
    
    try {
      toast.loading("Sending email with PO attachment...", { id: "sending-email" });
      
      const response = await axios.post(`${BACKEND_URL}/api/purchase-orders/send-email`, {
        po_id: poId,
        to_email: vendorEmail,
        to_name: vendorName || vendorCompanyName
      });
      
      toast.success(response.data.message, { id: "sending-email" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email", { id: "sending-email" });
    }
  };

  // Save PO and return ID (for email sending)
  const savePOAndGetId = async () => {
    const validItems = lineItems.filter(item => item.description);
    if (validItems.length === 0) {
      toast.error("Please add at least one item to the PO");
      return null;
    }

    try {
      const payload = {
        po_number: poNumber,
        po_date: poDate,
        delivery_date: deliveryDate,
        vendor_id: selectedVendorId,
        vendor_company_name: vendorCompanyName,
        vendor_name: vendorName,
        vendor_address: vendorAddress,
        vendor_gstin: vendorGstin,
        vendor_email: vendorEmail,
        vendor_phone: vendorPhone,
        enable_delivery: enableDeliveryDetails,
        delivery_name: deliveryName,
        delivery_address: deliveryAddress,
        delivery_gstin: deliveryGstin,
        items: validItems.map(item => ({
          description: item.description,
          hsn: item.hsn,
          qty: item.qty,
          unit: item.unit,
          rate: item.rate,
          amount: item.amount
        })),
        sub_total: subTotal,
        cgst_rate: cgstRate,
        cgst_amount: cgstAmount,
        sgst_rate: sgstRate,
        sgst_amount: sgstAmount,
        grand_total: grandTotal,
        status: "draft"
      };

      const response = await axios.post(`${BACKEND_URL}/api/purchase-orders`, payload);
      return response.data.id;
    } catch (error) {
      toast.error("Please save the PO first");
      return null;
    }
  };

  // Send PO via WhatsApp
  const handleSendWhatsApp = () => {
    if (!vendorPhone) {
      toast.error("Vendor phone not available. Please update vendor details in Database.");
      return;
    }
    
    // Clean phone number - remove spaces, dashes, and + sign for WhatsApp URL
    let cleanPhone = vendorPhone.replace(/[\s\-\+]/g, '');
    // If doesn't start with country code, assume India (+91)
    if (!cleanPhone.startsWith('91') && cleanPhone.length === 10) {
      cleanPhone = '91' + cleanPhone;
    }
    
    const message = encodeURIComponent(
      `*Purchase Order - ${poNumber}*\n` +
      `From: ${companyInfo.name}\n\n` +
      `Dear ${vendorName || vendorCompanyName},\n\n` +
      `📋 *PO Details:*\n` +
      `PO Number: ${poNumber}\n` +
      `PO Date: ${poDate}\n` +
      `Delivery Date: ${deliveryDate || 'To be confirmed'}\n\n` +
      `📦 *Items:*\n` +
      lineItems.filter(item => item.description).map((item, idx) => 
        `${idx + 1}. ${item.description}\n   Qty: ${item.qty} ${item.unit} | Rate: ₹${item.rate}\n   Amount: ₹${item.amount.toFixed(2)}`
      ).join('\n\n') +
      `\n\n💰 *Summary:*\n` +
      `Sub Total: ₹${subTotal.toFixed(2)}\n` +
      `CGST (${cgstRate}%): ₹${cgstAmount.toFixed(2)}\n` +
      `SGST (${sgstRate}%): ₹${sgstAmount.toFixed(2)}\n` +
      `*Grand Total: ₹${grandTotal.toFixed(2)}*\n\n` +
      `Please confirm the order. Thank you! 🙏`
    );
    
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
    toast.success("Opening WhatsApp...");
  };

  // Save PO
  const handleSavePO = async () => {
    if (!vendorCompanyName.trim()) {
      toast.error("Please enter vendor company name");
      return;
    }

    if (lineItems.filter(item => item.description).length === 0) {
      toast.error("Please add at least one line item");
      return;
    }

    try {
      const payload = {
        po_number: poNumber,
        po_date: poDate,
        delivery_date: deliveryDate,
        vendor_id: selectedVendorId,
        vendor_company_name: vendorCompanyName,
        vendor_name: vendorName,
        vendor_address: vendorAddress,
        vendor_gstin: vendorGstin,
        vendor_email: vendorEmail,
        vendor_phone: vendorPhone,
        enable_delivery: enableDeliveryDetails,
        delivery_name: deliveryName,
        delivery_address: deliveryAddress,
        delivery_gstin: deliveryGstin,
        items: lineItems.filter(item => item.description || item.selectedItem).map(item => ({
          selectedItem: item.selectedItem,
          description: item.description,
          hsn: item.hsn,
          qty: item.qty,
          unit: item.unit,
          rate: item.rate,
          amount: item.amount
        })),
        sub_total: subTotal,
        cgst_rate: cgstRate,
        cgst_amount: cgstAmount,
        sgst_rate: sgstRate,
        sgst_amount: sgstAmount,
        grand_total: grandTotal,
        notes: notes,
        terms_conditions: termsConditions,
        status: isEditMode ? undefined : "draft"
      };

      if (isEditMode && editId) {
        await axios.put(`${BACKEND_URL}/api/purchase-orders/${editId}`, payload);
        toast.success("Purchase Order updated successfully!");
      } else {
        await axios.post(`${BACKEND_URL}/api/purchase-orders`, payload);
        toast.success("Purchase Order saved successfully!");
      }
      navigate("/store/purchase-order");
    } catch (error) {
      if (error.response?.data?.detail?.includes("already exists")) {
        toast.error("PO number already exists. Please generate a new one.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to save Purchase Order");
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/store/purchase-order")}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-black text-amber-400 tracking-wider flex items-center gap-3">
              <FileText className="h-8 w-8" />
              {isEditMode ? "Edit Purchase Order" : "Create Purchase Order"}
            </h1>
            <p className="text-gray-400 mt-1">
              {isEditMode ? `Editing: ${poNumber}` : "Create new material request form"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canUseInventoryControl && (
            <Button
              onClick={() => navigate("/store/inventory-control")}
              variant="outline"
              className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20"
              data-testid="po-open-inventory-control"
            >
              <Boxes className="h-4 w-4 mr-2" />
              Inventory Control
            </Button>
          )}
          {!isEditMode && (
            <Button
              onClick={generatePONumber}
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              New PO #
            </Button>
          )}
          <Button
            onClick={handleSavePO}
            className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
          >
            <Save className="h-4 w-4 mr-2" />
            {isEditMode ? "Update PO" : "Save PO"}
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="outline"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
            disabled={!vendorEmail}
            title={vendorEmail ? `Send to ${vendorEmail}` : "Add vendor email in Database"}
          >
            <Mail className="h-4 w-4 mr-2" />
            Email
          </Button>
          <Button
            onClick={handleSendWhatsApp}
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/20"
            disabled={!vendorPhone}
            title={vendorPhone ? `Send to ${vendorPhone}` : "Add vendor phone in Database"}
          >
            <Phone className="h-4 w-4 mr-2" />
            WhatsApp
          </Button>
        </div>
      </div>

      {/* Company & Vendor Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Info */}
        <Card className="bg-black/60 border-2 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-400 flex items-center gap-2 text-lg">
              <img src={LOGO_URL} alt="CPI" className="h-8 w-8 rounded" />
              Company Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="font-bold text-white text-lg">{companyInfo.name}</p>
            <p className="text-gray-400">{companyInfo.location}</p>
            <p className="text-gray-400">Phone: {companyInfo.phone}</p>
            <p className="text-gray-400">Email: {companyInfo.email}</p>
            <p className="text-amber-400 font-mono text-xs">GSTIN: {companyInfo.gstin}</p>
          </CardContent>
        </Card>

        {/* Vendor Info */}
        <Card className="bg-black/60 border-2 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-400 text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Vendor Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-amber-300 text-sm">Select Vendor</Label>
              <Popover open={vendorDropdownOpen} onOpenChange={setVendorDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between bg-black/50 border-amber-500/30 text-white mt-1"
                  >
                    {vendorCompanyName || "Select vendor or type manually..."}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-gray-900 border-amber-500/30">
                  <Command className="bg-gray-900">
                    <CommandInput placeholder="Search vendors..." className="bg-gray-900 text-white" />
                    <CommandList>
                      <CommandEmpty>No vendor found. Type manually below.</CommandEmpty>
                      <CommandGroup className="max-h-48 overflow-auto">
                        {vendors.map((vendor) => (
                          <CommandItem
                            key={vendor.id}
                            onSelect={() => selectVendor(vendor)}
                            className="text-white hover:bg-amber-500/20 cursor-pointer"
                          >
                            <Check className={cn("mr-2 h-4 w-4", selectedVendorId === vendor.id ? "opacity-100" : "opacity-0")} />
                            <div>
                              <span className="font-medium">{vendor.company_name}</span>
                              {vendor.gstin && <span className="ml-2 text-xs text-gray-400">({vendor.gstin})</span>}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-amber-300 text-sm">Company Name *</Label>
              <Input
                value={vendorCompanyName}
                onChange={(e) => setVendorCompanyName(e.target.value)}
                placeholder="Vendor company name"
                className="bg-black/50 border-amber-500/30 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-sm">Contact Person</Label>
              <Input
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder="Contact name (optional)"
                className="bg-black/50 border-amber-500/30 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-sm">Vendor GSTIN</Label>
              <Input
                value={vendorGstin}
                onChange={(e) => setVendorGstin(e.target.value.toUpperCase())}
                placeholder="e.g., 33AAACN2359J1ZJ"
                className="bg-black/50 border-amber-500/30 text-white mt-1 font-mono"
                maxLength={15}
              />
            </div>
          </CardContent>
        </Card>

        {/* PO Details */}
        <Card className="bg-black/60 border-2 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-400 text-lg">PO Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-amber-300 text-sm">PO Number</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  className="bg-black/50 border-amber-500/30 text-white font-mono font-bold flex-1"
                  placeholder="PO/YY-YY/XXX"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={generatePONumber}
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                  title="Generate next PO number"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Format: PO/YY-YY/XXX (e.g., PO/25-26/081)</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-amber-300 text-sm">PO Date</Label>
                <Input
                  type="date"
                  value={poDate}
                  onChange={(e) => setPoDate(e.target.value)}
                  className="bg-black/50 border-amber-500/30 text-white mt-1 [color-scheme:dark]"
                />
              </div>
              <div>
                <Label className="text-amber-300 text-sm">Delivery Date</Label>
                <Input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className="bg-black/50 border-amber-500/30 text-white mt-1 [color-scheme:dark]"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delivery Details (Optional) */}
      <Card className="bg-black/60 border-2 border-amber-500/30">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-amber-400 text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Delivery Details (Optional)
            </CardTitle>
            <div className="flex items-center gap-3">
              <Label htmlFor="enable-delivery" className="text-amber-300 text-sm cursor-pointer">
                Enable different delivery address
              </Label>
              <Switch
                id="enable-delivery"
                checked={enableDeliveryDetails}
                onCheckedChange={setEnableDeliveryDetails}
                className="data-[state=checked]:bg-amber-500"
              />
            </div>
          </div>
        </CardHeader>
        {enableDeliveryDetails && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-amber-300 text-sm">Delivery Name (Select Vendor)</Label>
                <Popover open={deliveryVendorOpen} onOpenChange={setDeliveryVendorOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={deliveryVendorOpen}
                      className="w-full justify-between bg-black/50 border-amber-500/30 text-white mt-1 hover:bg-black/70"
                    >
                      {deliveryName || "Search vendor..."}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0 bg-gray-900 border-amber-500/30">
                    <Command className="bg-gray-900">
                      <CommandInput 
                        placeholder="Search vendor..." 
                        value={deliveryVendorSearch}
                        onValueChange={setDeliveryVendorSearch}
                        className="bg-gray-900 text-white"
                      />
                      <CommandList>
                        <CommandEmpty>No vendor found.</CommandEmpty>
                        <CommandGroup className="max-h-60 overflow-auto">
                          {vendors.filter(v => 
                            !deliveryVendorSearch || 
                            v.company_name?.toLowerCase().includes(deliveryVendorSearch.toLowerCase()) ||
                            v.vendor_name?.toLowerCase().includes(deliveryVendorSearch.toLowerCase())
                          ).map((vendor) => (
                            <CommandItem
                              key={vendor.id}
                              value={vendor.company_name}
                              onSelect={() => {
                                setDeliveryName(vendor.vendor_name || vendor.company_name);
                                setDeliveryAddress(vendor.address || "");
                                setDeliveryGstin(vendor.gstin || "");
                                setDeliveryVendorOpen(false);
                                setDeliveryVendorSearch("");
                              }}
                              className="text-white hover:bg-amber-500/20 cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  deliveryName === (vendor.vendor_name || vendor.company_name) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div>
                                <p className="font-medium">{vendor.company_name}</p>
                                {vendor.vendor_name && <p className="text-xs text-gray-400">{vendor.vendor_name}</p>}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-amber-300 text-sm">Delivery Address</Label>
                <Input
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Auto-filled from vendor"
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-amber-300 text-sm">Delivery GSTIN</Label>
                <Input
                  value={deliveryGstin}
                  onChange={(e) => setDeliveryGstin(e.target.value.toUpperCase())}
                  placeholder="Auto-filled from vendor"
                  className="bg-black/50 border-amber-500/30 text-white mt-1 font-mono"
                  maxLength={15}
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Quick Add from Low Stock */}
      {lowStockItems.length > 0 && (
        <Card className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border-2 border-red-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-400 text-lg flex items-center gap-2">
              Quick Add Low Stock Items
              <span className="text-xs bg-red-500/30 px-2 py-1 rounded-full">{lowStockItems.length} items</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {lowStockItems.slice(0, 8).map((item) => (
                <Button
                  key={item.product_code || item.id}
                  variant="outline"
                  size="sm"
                  className="border-red-500/50 text-red-300 hover:bg-red-500/20"
                  onClick={() => {
                    const itemName = `${item.product_code} - ${item.product_type || ''}`;
                    const newItem = {
                      _uid: newRowUid(),
                      id: lineItems.length + 1,
                      selectedItem: itemName,
                      description: `${itemName} (Need: ${item.min_stock - item.current_stock})`,
                      hsn: item.hsn_code || "48043100",
                      qty: String(item.min_stock - item.current_stock),
                      unit: "kg",
                      rate: "",
                      amount: 0
                    };
                    setLineItems([...lineItems, newItem]);
                    toast.success(`Added ${item.product_code} to PO`);
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {item.product_code} (-{item.min_stock - item.current_stock})
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Items */}
      <Card className="bg-black/60 border-2 border-amber-500/30">
        <CardHeader>
          <CardTitle className="text-amber-400 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Line Items
            </span>
            <div className="flex items-center gap-2">
              {canUseInventoryControl && (
                <Button
                  onClick={() => navigate("/store/inventory-control")}
                  size="sm"
                  variant="outline"
                  className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20"
                  data-testid="po-line-items-open-inventory-control"
                >
                  <Boxes className="h-4 w-4 mr-1" />
                  Calculate from Board Spec
                </Button>
              )}
              <Button
                onClick={addLineItem}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-amber-500/30">
                  <th className="text-left py-3 px-2 text-amber-300 w-10">#</th>
                  <th className="text-left py-3 px-2 text-amber-300">Item & Description</th>
                  <th className="text-left py-3 px-2 text-amber-300 w-32">HSN/SAC</th>
                  <th className="text-left py-3 px-2 text-amber-300 w-24">Qty</th>
                  <th className="text-left py-3 px-2 text-amber-300 w-20">Unit</th>
                  <th className="text-left py-3 px-2 text-amber-300 w-24">Rate (₹)</th>
                  <th className="text-right py-3 px-2 text-amber-300 w-28">Amount</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => (
                  <tr key={item._uid || item.id} className="border-b border-amber-500/20">
                    <td className="py-2 px-2 text-gray-400 align-top">{idx + 1}</td>
                    <td className="py-2 px-2">
                      <div className="space-y-2">
                        <Popover open={itemDropdownOpen[idx]} onOpenChange={(open) => setItemDropdownOpen({ ...itemDropdownOpen, [idx]: open })}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between bg-black/50 border-amber-500/30 text-white hover:bg-black/70 h-9 text-sm"
                            >
                              {item.selectedItem || "Select item..."}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[350px] p-0 bg-gray-900 border-amber-500/30">
                            <Command className="bg-gray-900">
                              <CommandInput
                                placeholder="Search materials..."
                                className="bg-gray-900 text-white"
                              />
                              <CommandList>
                                <CommandEmpty>No item found</CommandEmpty>
                                <CommandGroup className="max-h-40 overflow-auto">
                                  {materials.map((m) => (
                                    <CommandItem
                                      key={m.id}
                                      onSelect={() => selectMaterial(idx, m)}
                                      className="text-white hover:bg-amber-500/20 cursor-pointer"
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          item.selectedItem?.includes(m.product_code || m.item_name) ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      {m.product_code || m.item_name} - {m.product_type} {m.reel_size_inches ? `(${m.reel_size_inches}")` : ''}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {/* Description text area */}
                        <Input
                          value={item.description}
                          onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                          placeholder="Add description / specifications..."
                          className="bg-black/30 border-amber-500/20 text-white h-8 text-xs"
                          data-testid={`po-line-description-${idx}`}
                        />
                        {/* Inline size + unit prompt — appears after an item
                            is selected. Stays visible while the user is
                            typing (don't use !item.size as the toggle — that
                            caused the input to vanish after the first
                            keystroke, breaking entry). Once the user blurs
                            the size field OR picks a unit, we lock the row:
                            inputs hide, size folds into description, and a
                            small "Change size" link replaces them. */}
                        {item.selectedItem && (!item.sizeLocked ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] text-amber-400/70 uppercase tracking-wide">Size:</span>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={item.size || ""}
                              onChange={(e) => updateLineItem(idx, 'size', e.target.value)}
                              onBlur={(e) => {
                                // If user is tabbing/clicking into the unit Select trigger, defer lock
                                // so they can pick a unit first.
                                const next = e.relatedTarget;
                                if (next && next.closest && next.closest(`[data-testid="po-line-size-unit-${idx}"]`)) return;
                                if (e.target.value) updateLineItem(idx, '_lockSize', true);
                              }}
                              placeholder="e.g. 52"
                              className="bg-black/30 border-amber-500/20 text-white h-7 text-xs w-20"
                              data-testid={`po-line-size-input-${idx}`}
                              autoFocus={!item.size}
                            />
                            <Select
                              value={item.sizeUnit || "Inches"}
                              onValueChange={(v) => updateLineItem(idx, 'sizeUnit', v)}
                            >
                              <SelectTrigger className="bg-black/30 border-amber-500/20 text-white h-7 w-[80px] px-2 text-xs" data-testid={`po-line-size-unit-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-900 border-amber-500/30">
                                <SelectItem value="mm">mm</SelectItem>
                                <SelectItem value="cm">cm</SelectItem>
                                <SelectItem value="Inches">Inches</SelectItem>
                              </SelectContent>
                            </Select>
                            {item.size && (
                              <button
                                type="button"
                                onClick={() => updateLineItem(idx, '_lockSize', true)}
                                className="text-[10px] px-2 py-0.5 rounded bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30"
                                data-testid={`po-line-size-confirm-${idx}`}
                              >
                                ✓ Add
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => updateLineItem(idx, '_clearSize', true)}
                            className="text-[10px] text-amber-400/60 hover:text-amber-300 underline-offset-2 hover:underline text-left"
                            data-testid={`po-line-size-edit-${idx}`}
                          >
                            ✎ Change size ({item.size} {item.sizeUnit})
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <Popover open={hsnDropdownOpen[idx]} onOpenChange={(open) => setHsnDropdownOpen({ ...hsnDropdownOpen, [idx]: open })}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            className="w-full justify-between bg-black/50 border-amber-500/30 text-white hover:bg-black/70 h-9 text-sm font-mono"
                          >
                            {item.hsn || "HSN..."}
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[250px] p-0 bg-gray-900 border-amber-500/30">
                          <Command className="bg-gray-900">
                            <CommandInput placeholder="Search HSN..." className="bg-gray-900 text-white" />
                            <CommandList>
                              <CommandEmpty>No HSN found</CommandEmpty>
                              <CommandGroup className="max-h-40 overflow-auto">
                                {hsnCodes.map((hsn) => (
                                  <CommandItem
                                    key={hsn.id}
                                    onSelect={() => selectHSN(idx, hsn)}
                                    className="text-white hover:bg-amber-500/20 cursor-pointer"
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", item.hsn === hsn.code ? "opacity-100" : "opacity-0")} />
                                    <span className="font-mono">{hsn.code}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        type="number"
                        value={item.qty}
                        onChange={(e) => updateLineItem(idx, 'qty', e.target.value)}
                        placeholder="Qty"
                        className="bg-black/50 border-amber-500/30 text-white h-9 text-sm"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Select value={item.unit} onValueChange={(v) => updateLineItem(idx, 'unit', v)}>
                        <SelectTrigger className="bg-black/50 border-amber-500/30 text-white h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-amber-500/30">
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="pcs">pcs</SelectItem>
                          <SelectItem value="nos">nos</SelectItem>
                          <SelectItem value="roll">roll</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateLineItem(idx, 'rate', e.target.value)}
                        placeholder="Rate"
                        className="bg-black/50 border-amber-500/30 text-white h-9 text-sm"
                      />
                    </td>
                    <td className="py-2 px-2 text-right font-bold text-amber-400">
                      {formatCurrency(item.amount)}
                    </td>
                    <td className="py-2 px-2">
                      {lineItems.length > 1 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeLineItem(idx)}
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-6 flex justify-end">
            <div className="w-72 space-y-2">
              <div className="flex justify-between py-2 border-b border-gray-700">
                <span className="text-gray-400">Sub Total</span>
                <span className="text-white font-bold">{formatCurrency(subTotal)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-700">
                <span className="text-gray-400">CGST ({cgstRate}%)</span>
                <span className="text-white">{formatCurrency(cgstAmount)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-700">
                <span className="text-gray-400">SGST ({sgstRate}%)</span>
                <span className="text-white">{formatCurrency(sgstAmount)}</span>
              </div>
              <div className="flex justify-between py-3 bg-amber-500/20 rounded-lg px-4 mt-2">
                <span className="text-amber-400 font-bold text-lg">Grand Total</span>
                <span className="text-amber-400 font-black text-lg">{formatCurrency(grandTotal)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tax Settings */}
      <Card className="bg-black/60 border-2 border-gray-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-gray-400 text-sm">Tax Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-gray-400 text-sm">CGST Rate:</Label>
              <Input
                type="number"
                value={cgstRate}
                onChange={(e) => setCgstRate(parseFloat(e.target.value) || 0)}
                className="bg-black/50 border-gray-500/30 text-white w-20 h-8"
              />
              <span className="text-gray-500">%</span>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-gray-400 text-sm">SGST Rate:</Label>
              <Input
                type="number"
                value={sgstRate}
                onChange={(e) => setSgstRate(parseFloat(e.target.value) || 0)}
                className="bg-black/50 border-gray-500/30 text-white w-20 h-8"
              />
              <span className="text-gray-500">%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes Section */}
      <Card className="bg-black/60 border-2 border-blue-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-blue-400 text-sm flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any special instructions, notes, or remarks for the vendor..."
            className="bg-black/50 border-blue-500/30 text-white min-h-[80px]"
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Terms & Conditions Section */}
      <Card className="bg-black/60 border-2 border-purple-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-purple-400 text-sm flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Terms & Conditions
            <span className="ml-2 px-2 py-0.5 bg-purple-500/20 rounded text-xs">
              {termsConditions.length} items
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Add New Term */}
          <div className="flex gap-2">
            <Input
              value={newTerm}
              onChange={(e) => setNewTerm(e.target.value)}
              placeholder="Add new term or condition..."
              className="bg-black/50 border-purple-500/30 text-white flex-1"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newTerm.trim()) {
                  setTermsConditions([...termsConditions, newTerm.trim()]);
                  setNewTerm("");
                }
              }}
            />
            <Button
              onClick={() => {
                if (newTerm.trim()) {
                  setTermsConditions([...termsConditions, newTerm.trim()]);
                  setNewTerm("");
                }
              }}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Terms List */}
          {termsConditions.length > 0 && (
            <div className="space-y-2 mt-3">
              {termsConditions.map((term, idx) => (
                <div key={`${idx}-${term.slice(0,20)}`} className="flex items-start gap-2 p-2 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <span className="text-purple-400 font-bold w-6">{idx + 1}.</span>
                  {editingTermIndex === idx ? (
                    <div className="flex-1 flex gap-2">
                      <Input
                        value={editingTermText}
                        onChange={(e) => setEditingTermText(e.target.value)}
                        className="bg-black/50 border-purple-500/30 text-white flex-1 h-8"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          if (editingTermText.trim()) {
                            const updated = [...termsConditions];
                            updated[idx] = editingTermText.trim();
                            setTermsConditions(updated);
                          }
                          setEditingTermIndex(null);
                          setEditingTermText("");
                        }}
                        className="bg-green-500 hover:bg-green-600 text-white h-8"
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingTermIndex(null);
                          setEditingTermText("");
                        }}
                        className="text-gray-400 h-8"
                      >
                        ✕
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className="text-gray-300 flex-1">{term}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingTermIndex(idx);
                          setEditingTermText(term);
                        }}
                        className="text-blue-400 hover:text-blue-300 h-6 w-6 p-0"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setTermsConditions(termsConditions.filter((_, i) => i !== idx));
                        }}
                        className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Default Terms Buttons */}
          <div className="pt-2 border-t border-purple-500/20">
            <p className="text-gray-500 text-xs mb-2">Quick Add Default Terms:</p>
            <div className="flex flex-wrap gap-2">
              {[
                "Payment within 30 days of invoice",
                "Goods once sold cannot be returned",
                "Delivery charges extra",
                "Subject to Chennai jurisdiction"
              ].map((defaultTerm) => (
                <Button
                  key={defaultTerm}
                  size="sm"
                  variant="outline"
                  className="text-xs border-purple-500/30 text-purple-300 hover:bg-purple-500/20"
                  onClick={() => {
                    if (!termsConditions.includes(defaultTerm)) {
                      setTermsConditions([...termsConditions, defaultTerm]);
                    }
                  }}
                >
                  + {defaultTerm.substring(0, 25)}...
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Control hand-off — confirm before append */}
      <AlertDialog open={prefillDialogOpen} onOpenChange={setPrefillDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border-cyan-500/30" data-testid="po-prefill-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-cyan-300 flex items-center gap-2">
              <Boxes className="h-5 w-5" />
              Import {prefillPayload?.items?.length || 0} item(s) from Inventory Control?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              These calculated raw-material rows will be <b>appended</b> to your existing line items.
              Materials matching an existing Store product code will auto-link; new ones will be auto-created in Store inventory.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {prefillPayload?.items?.length > 0 && (
            <div className="max-h-48 overflow-auto rounded border border-gray-700 bg-black/40 p-2 text-xs font-mono">
              {prefillPayload.items.map((it, i) => (
                <div key={`${it.rmName}-${i}`} className="flex justify-between py-0.5 text-gray-300">
                  <span className="text-cyan-300">{it.rmName}</span>
                  <span>{it.qtyKg} kg</span>
                </div>
              ))}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={cancelInventoryControlPrefill}
              className="bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700"
              data-testid="po-prefill-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); applyInventoryControlPrefill(); }}
              disabled={prefillApplying}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="po-prefill-append"
            >
              {prefillApplying ? "Appending..." : "Append items"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StorePurchaseOrder;
